import { React, useState, useEffect} from 'react'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Form } from "react-bootstrap";
import { validateAndFocusForm } from '../../../utils/surveyValidationUtils';
import { useRealTimeValidation } from '../../../utils/realTimeValidationUtils';

import VAS from "../../../components/SelfAssessment/VAS/VAS";
import ASESElbow from '../../../components/SelfAssessment/ASESElbow/ASESElbow';
import ASESShoulder from '../../../components/SelfAssessment/ASESShoulder/ASESShoulder';
import SANE from "../../../components/SelfAssessment/SANE/SANE";
import VR12 from '../../../components/SelfAssessment/VR12/VR12';
import StandardPreOp from '../../../components/SelfAssessment/StandardPreOp/StandardPreOp';
import FormCompletion from '../../../components/SelfAssessment/FormCompletion/FormCompletion';
import CustomProgressBar from '../../../components/CustomProgressBar/CustomProgressBar';
import { BASE_URL } from "../../../utils/constants";

import './PreTreatment.scss'
import '../../../styles/survey-design-system.scss';
import '../../../styles/survey-validation.scss';
import TermsAndConditions from '../../../components/SelfAssessment/TermsAndConditions/TermsAndConditions';
import SurveyAlreadyCompleted from '../../../components/SelfAssessment/SurveyAlreadyCompleted/SurveyAlreadyCompleted';

const PreTreatment = () => {
  const numberOfSteps = 5;
  const [termsAndConditionsStatus, setTermsAndConditionsStatus] = useState({completed: false, accepted: false})
  const [surveyCompleted, setSurveyCompleted] = useState(false)
  const [isCheckingCompletion, setIsCheckingCompletion] = useState(true)
  const [step, setStep] = useState(1);

  const { eventId, customPatientId } = useParams();
  const treatmentSide = useParams()["treatmentSide"] === "left"? "Links" : "Rechts"
  const study = useParams()["study"].replace("-", " ")
  const navigate = useNavigate();
  useEffect(() => {
    if (!["elbow", "shoulder arthroscopy", "shoulder arthroplasty"].includes(study)) {
      navigate("/404")
    }
  });
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    vasScore: "",
    saneScore: "",
    ases: {
      asesQuestion1: "",
      asesQuestion2: "",
      asesQuestion3: "",
      asesQuestion4: "",
      asesQuestion5: "",
      asesQuestion6: "",
      asesQuestion7: "",
      asesQuestion8: "",
      asesQuestion9: "",
      asesQuestion10: "",
      asesQuestion11: "",
      asesQuestion12: "",
      asesQuestion13: "",
      asesQuestion14: "",
      asesQuestion15: "",
      asesQuestion16: "",
      asesQuestion17: "",
      asesQuestion18: ""
    },
    vr12: {
      vr12Question1: "",
      vr12Question2: "",
      vr12Question3: "",
      vr12Question4: "",
      vr12Question5: "",
      vr12Question6: "",
      vr12Question7: "",
      vr12Question8: "",
      vr12Question9: "",
      vr12Question10: "",
      vr12Question11: "",
      vr12Question12: "",
      vr12Question13: "",
      vr12Question14: ""
    },
    spreop: {
      spreopQuestion1: "",
      spreopQuestion2: "",
      spreopQuestion3: "",
      spreopQuestion4: ""
    }
  })
  
  useEffect(() => {
    const checkSurveyCompletion = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/v1/self-assessment/check-survey-completion/${customPatientId}/${eventId}/pre_treatment`)
        setSurveyCompleted(response.data.completed)
      }
      catch (error) {
        console.log(error)
      }
      finally {
        setIsCheckingCompletion(false)
      }
    }
    checkSurveyCompletion()
  }, [customPatientId, eventId])


  const handleTermsAndConditionsAccept = () => {
    setTermsAndConditionsStatus({
      completed: true,
      accepted: true
    })
  }

  const handleTermsAndConditionsReject = async () => {
    setTermsAndConditionsStatus({
      completed: true,
      accepted: false
    })
    const data = {
      customPatientId: customPatientId,
      eventId: eventId
    }
    try {
      await axios.post(`${BASE_URL}/api/v1/self-assessment/terms-and-conditions-rejected`, data)

      setStep(numberOfSteps + 1)
    }
    catch (error) {
      console.log(error)
    }
  }
  
  const nextStep = () => {
    clearAllFieldValidations();
    setFieldErrors({});
    setTouchedFields({});
    const validationResult = validateAndFocusForm(formData, step, study, {
      clearPreviousErrors: true,
      showVisualFeedback: true,
      scrollToFirst: true
    });

    if (validationResult.isValid) {
      setValidated(false);
      setStep(step + 1);
    } else {
      setValidated(true);
      setValidationErrors(validationResult.invalidFields);
      setFieldErrors(validationResult.fieldErrors);
      const touched = Object.keys(validationResult.fieldErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields(touched);
      
    }
  };

  const prevStep = () => {
    clearAllFieldValidations();
    setValidationErrors([]);
    setStep(step - 1);
  };

  const { validateFieldRealTime, clearAllFieldValidations } = useRealTimeValidation(
    formData, step, study, setFieldErrors, setTouchedFields
  );

  const handleInputData = (type, input, value) => {
    let updatedFormData;
    switch(type) {
      case "ases":
        updatedFormData = { ...formData, ases: { ...formData.ases, [input]: value } };
        break;
      case "vr12":
        updatedFormData = { ...formData, vr12: { ...formData.vr12, [input]: value } };
        break;
      case "spreop":
        updatedFormData = { ...formData, spreop: { ...formData.spreop, [input]: value } };
        break;
      default:
        updatedFormData = { ...formData, [input]: value };
    }
    setFormData(updatedFormData);

    const fieldPath = (type === 'vas' || type === 'sane') ? input : `${type}.${input}`;
    validateFieldRealTime(fieldPath, value);

    if (validated) {
      setTimeout(() => {
        const validationResult = validateAndFocusForm(
          updatedFormData,
          step,
          study,
          {
            clearPreviousErrors: true,
            showVisualFeedback: true,
            scrollToFirst: false,
          }
        );
        setValidationErrors(validationResult.invalidFields);
        setFieldErrors(validationResult.fieldErrors);
      }, 0);
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    clearAllFieldValidations();
    const validationResult = validateAndFocusForm(formData, step, study, {
      clearPreviousErrors: true,
      showVisualFeedback: true,
      scrollToFirst: true
    });

    if (validationResult.isValid) {
      setIsSubmitting(true);
      const data = {
        eventId: eventId,
        customPatientId: customPatientId,
        study: study,
        termsAndConditionsAccepted: termsAndConditionsStatus.accepted,
        formAnswers: formData
      };
      try {
        await axios.post(`${BASE_URL}/api/v1/self-assessment/pre-treatment`, data)
        setStep(numberOfSteps + 1);
      }
      catch(error) {
        console.log(error.response.data)
      }
      finally {
        setIsSubmitting(false);
      }
    } else {
      setValidated(true);
      setValidationErrors(validationResult.invalidFields);
      setFieldErrors(validationResult.fieldErrors);
      const touched = Object.keys(validationResult.fieldErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields(touched);
      
    }
  };

  const renderFormContent = step => {
    switch (step){
      case 1:
        return <VAS handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      case 2:
        if(study === "elbow") {
          return <ASESElbow handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
        }
        else {
          return <ASESShoulder handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
        }
      case 3:
        return <SANE handleFormData={handleInputData} study={study} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      case 4:
        return <StandardPreOp handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      case 5:
        return <VR12 handleFormData={handleInputData} values={formData} fieldErrors={fieldErrors} touchedFields={touchedFields} />
      case 6:
        return <FormCompletion />
      default:
        return <div></div>
    }
  }

  return (
    <div className="modern-survey-container">
      <Form onSubmit={handleSubmit} validated={validated}>
        <div className="modern-survey-card">
          <div className="modern-survey-header">
            <div className="patient-identifier">
              <span className="patient-id-label">Patiënt:</span>
              <span className="patient-id-value">{customPatientId}</span>
            </div>
            <h1 className="survey-title">
              {study === "elbow"? "Elleboog" : "Schouder"} ({treatmentSide})
            </h1>
            <p className="survey-subtitle">Pre-behandeling Zelfevaluatie</p>
          </div>
          {isCheckingCompletion ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              Laden...
            </div>
          ) : !surveyCompleted? 
            (
              <>
                {!termsAndConditionsStatus.completed? 
                  (
                    <TermsAndConditions handleTermsAndConditionsAccept={handleTermsAndConditionsAccept} handleTermsAndConditionsReject={handleTermsAndConditionsReject}/>
                  )
                  :
                  (
                    <>
                      {termsAndConditionsStatus.accepted ?
                      (
                        <div className="modern-progress-container">
                          <CustomProgressBar className="form-progress-bar" currentStep={step} numberOfSteps={numberOfSteps} />
                        </div>
                      ) : null}
                      <div className="modern-survey-body">
                        {validationErrors.length > 0 && (
                          <div className="validation-summary" role="alert">
                            <h3 className="summary-title">
                              Er zijn fouten gevonden
                            </h3>
                            <ul className="summary-list">
                              {validationErrors.map((error, index) => (
                                <li key={index}>
                                  <button
                                    type="button"
                                    className="error-link"
                                    onClick={() => {
                                      const field = document.querySelector(error.selector);
                                      if (field) {
                                        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        field.focus();
                                      }
                                    }}
                                  >
                                    {error.message}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <Container className="form-content">
                          {
                            renderFormContent(step)
                          }
                        </Container>
                      </div>
                      {step < (numberOfSteps + 1) ? (
                        <div className={`modern-survey-footer ${step === 1 ? 'single-button' : ''}`}>
                          {step !== 1 && (
                            <button
                              type="button"
                              className="modern-btn modern-btn-secondary"
                              onClick={prevStep}
                              disabled={isSubmitting}
                            >
                              Vorige Stap
                            </button>
                          )}
                          {step < numberOfSteps && (
                            <button
                              type="button"
                              className="modern-btn modern-btn-primary"
                              onClick={nextStep}
                              disabled={isSubmitting}
                            >
                              Volgende Stap
                            </button>
                          )}
                          {step === numberOfSteps && (
                            <button
                              type="submit"
                              className={`modern-btn modern-btn-primary ${isSubmitting ? 'modern-btn-loading' : ''}`}
                              onClick={handleSubmit}
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? 'Bezig met indienen...' : 'Indienen'}
                            </button>
                          )}
                        </div>
                      ) : null}
                    </>
                  )
                }
              </>
              ) :
            (
              <SurveyAlreadyCompleted />
            )
          }
        </div>
      </Form>
    </div>
  )
}

export default PreTreatment;